/**
 * Terms acceptance gate, client side (ALL-5722, specs/terms-acceptance-gate S-10).
 *
 * While a person has not accepted the current terms version, every
 * session-verified API answers 403 `AUTH_TERMS_ACCEPTANCE_REQUIRED`. On first
 * load the shell learns it from `GET /auth/session` (`termsAcceptanceRequired`).
 * Mid-session, when Alloy publishes a new version, the first API call that
 * answers this 403 re-runs the session check (single flight), and the store
 * then shows the terms page in place of the app.
 *
 * ALL-5747 (specs/alloy-mfa S-3, S-13): the same single-flight re-check runs
 * for a 403 `AUTH_MFA_REQUIRED` (the session needs its Alloy second factor);
 * the store then shows the 2FA page instead. The backend checks MFA before
 * terms, and GET /auth/session reports both, so one re-check serves either.
 *
 * Like session-expiry.ts, the shell registers one handler, so this module needs
 * neither React, the router nor the store and is testable on its own. Nothing
 * here changes an auth control: the server decides who is blocked; this only
 * chooses which page to show.
 */
import { safeReturnPath } from "@/lib/session-expiry";

export const TERMS_REQUIRED_REASON = "AUTH_TERMS_ACCEPTANCE_REQUIRED";
/** ALL-5747: a session-verified route refused because the session's second factor is pending. */
export const MFA_REQUIRED_REASON = "AUTH_MFA_REQUIRED";
/** The 403 reason codes that re-check the session: each makes the store show a blocking page. */
const SESSION_GATE_REASONS: ReadonlySet<string> = new Set([TERMS_REQUIRED_REASON, MFA_REQUIRED_REASON]);
export const TERMS_PATH = "/terms";
/**
 * At most one session re-check per this window. If GET /auth/session says the
 * terms are accepted while an API still answers the 403 (a deployment
 * inconsistency), re-checking on every 403 would remount the page in a loop.
 */
export const TERMS_RECHECK_GUARD_MS = 30_000;

interface TermsGateConfig {
  /** Only an app that is showing product pages flips to the terms page. */
  isArmed: () => boolean;
  /** Re-runs the session check (the store's initSession). */
  onRequired: () => Promise<void> | void;
  now?: () => number;
}

let config: TermsGateConfig | null = null;
let inFlight: Promise<boolean> | null = null;
let lastRecheck = Number.NEGATIVE_INFINITY;

/** Registers the shell's handler. Returns an unregister function. */
export function configureTermsGate(next: TermsGateConfig): () => void {
  config = next;
  return () => {
    if (config === next) config = null;
  };
}

/** Test helper: forget every module-level state. */
export function resetTermsGateForTests(): void {
  config = null;
  inFlight = null;
  lastRecheck = Number.NEGATIVE_INFINITY;
}

/** Reads the reason code of a 403 body without consuming the caller's body. */
export async function forbiddenReasonCode(response: Response): Promise<string | null> {
  try {
    const body = (await response.clone().json()) as Record<string, unknown> | null;
    if (!body || typeof body !== "object") return null;
    const reason = body.reasonCode ?? body.reason_code;
    return typeof reason === "string" ? reason : null;
  } catch {
    return null;
  }
}

/**
 * Called by the API client for every 403. Resolves true when this call (or a
 * concurrent one) asked the shell to re-check the session for the terms or
 * MFA gate.
 */
export async function reportForbidden(_path: string, response: Response): Promise<boolean> {
  if (response.status !== 403) return false;
  const active = config;
  if (!active || !active.isArmed()) return false;
  // forbiddenReasonCode clones synchronously, before the caller reads the body.
  const code = await forbiddenReasonCode(response);
  if (code === null || !SESSION_GATE_REASONS.has(code)) return false;
  if (inFlight) return inFlight;
  if (config !== active || !active.isArmed()) return false;
  const now = (active.now ?? Date.now)();
  if (now - lastRecheck < TERMS_RECHECK_GUARD_MS) return false;
  lastRecheck = now;
  inFlight = Promise.resolve()
    .then(() => active.onRequired())
    .then(
      () => true,
      () => false,
    )
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/** Where /terms sends the person after acceptance: a validated same-origin path, else "/". */
export function termsReturnPath(search: string): string {
  let value: string | null = null;
  try {
    value = new URLSearchParams(search).get("return_to");
  } catch {
    value = null;
  }
  const safe = safeReturnPath(value);
  const pathname = safe.split(/[?#]/)[0];
  return pathname === TERMS_PATH || pathname.startsWith(`${TERMS_PATH}/`) ? "/" : safe;
}

/**
 * Full browser navigation after acceptance on /terms. A return path may belong
 * to admin-portal (/admin/) or enterprise-wallet (/wallet/), which are separate
 * apps on this origin, so the router cannot be used. An object so tests can spy.
 */
export const termsNavigation = {
  assign(url: string): void {
    window.location.assign(url);
  },
};
